import { type Either, Http, type HttpError, type HttpResponse, type IO } from "functype"

import type { ApiResponse } from "./apiEnvelope"

/**
 * Civala API client built on functype `Http`.
 *
 * Two result shapes for two call styles:
 *   - `get` / `post` / `put` return `Promise<T>` and **throw** on error —
 *     they plug straight into TanStack Query's `queryFn` (investor).
 *   - `getEither` / `postEither` / `putEither` return
 *     `Promise<Either<HttpError, T>>` and **never throw** — for imperative
 *     call sites that branch on the result (nexus pipeline hooks).
 *
 * The per-request `decode` is the single home for wire → domain mapping
 * (envelope unwrap + null→Option lifting), so consumers never touch raw
 * `null`. When omitted it defaults to a pass-through cast (the wire shape is
 * the domain shape) — the type argument names the contract.
 *
 * Cross-cutting concerns compose on the IO chain rather than via a `fetch`
 * override:
 *   - response logging / metrics  → `onResponse` (runs in `.tap`)
 *   - error logging / reporting   → `onError` (runs in `.mapError`)
 *   - request-side mutation (auth refresh, request IDs) → `HttpClientConfig`
 *     gained an effectful `beforeRequest` in functype 1.0.1; thread it into
 *     the `Http.client(...)` call below when a concrete need appears.
 *     Tracked: civala-ai/civala.ai#73. We have none today (Cloudflare Access
 *     handles auth via cookies; `meta.requestId` comes from the response).
 */

export type ResponseInfo = {
  readonly method: string
  readonly url: string
  readonly status: number
  /**
   * Best-effort request id from the `x-request-id` response header. The
   * envelope also carries `meta.requestId` in the body, but `validate`
   * collapses the response to the inner domain `data`, so the body-level id
   * isn't threaded here — use the header if the API sets it.
   */
  readonly requestId: string | undefined
}

export type ApiClientConfig = {
  readonly baseUrl: string
  readonly defaultHeaders?: Record<string, string>
  /** Invoked once per successful response (in `.tap`) — logging / metrics. */
  readonly onResponse?: (info: ResponseInfo) => void
  /** Invoked once per error before it propagates (in `.mapError`). */
  readonly onError?: (error: HttpError) => void
}

/**
 * Per-request options. `decode` maps the unwrapped envelope `data` to the
 * domain type — this is where nullable wire fields become `Option`. Omit it
 * to pass the data through unchanged. `signal` threads cancellation (e.g.
 * from TanStack Query's `queryFn` context).
 */
export type RequestOptions<T> = {
  readonly signal?: AbortSignal
  readonly decode?: (data: unknown) => T
}

export type ApiClient = {
  // Throwing — for TanStack Query.
  readonly get: <T>(path: string, options?: RequestOptions<T>) => Promise<T>
  readonly post: <T>(path: string, body: unknown, options?: RequestOptions<T>) => Promise<T>
  readonly put: <T>(path: string, body: unknown, options?: RequestOptions<T>) => Promise<T>
  /** DELETE carries no body — the resource is named by the path. */
  readonly del: <T>(path: string, options?: RequestOptions<T>) => Promise<T>
  // Either — for imperative call sites that branch on the result.
  readonly getEither: <T>(path: string, options?: RequestOptions<T>) => Promise<Either<HttpError, T>>
  readonly postEither: <T>(path: string, body: unknown, options?: RequestOptions<T>) => Promise<Either<HttpError, T>>
  readonly putEither: <T>(path: string, body: unknown, options?: RequestOptions<T>) => Promise<Either<HttpError, T>>
  readonly delEither: <T>(path: string, options?: RequestOptions<T>) => Promise<Either<HttpError, T>>
}

export const createApiClient = (config: ApiClientConfig): ApiClient => {
  const http = Http.client({
    baseUrl: config.baseUrl,
    defaultHeaders: { "Content-Type": "application/json", ...config.defaultHeaders },
  })

  // Compose envelope unwrap with the caller's domain decode (or a pass-through
  // default) so each request's `decode` only deals with the inner `data`
  // shape, never `{ data, meta }`.
  const validate =
    <T>(decode: RequestOptions<T>["decode"]) =>
    (raw: unknown): T => {
      const { data } = raw as ApiResponse<unknown>
      return decode ? decode(data) : (data as T)
    }

  const reqOpts = <T>(options?: RequestOptions<T>) => ({
    signal: options?.signal,
    validate: validate(options?.decode),
  })

  // Shared IO chain: response hook → error hook → unwrap to domain `data`.
  const chain = <T>(io: IO<never, HttpError, HttpResponse<T>>, method: string, url: string): IO<never, HttpError, T> =>
    io
      .tap((res) =>
        config.onResponse?.({
          method,
          url,
          status: res.status,
          requestId: res.headers.get("x-request-id") ?? undefined,
        }),
      )
      .mapError((err) => {
        config.onError?.(err)
        return err
      })
      .map((res) => res.data)

  return {
    get: (path, options) => chain(http.get(path, reqOpts(options)), "GET", path).runOrThrow(),
    post: (path, body, options) => chain(http.post(path, { body, ...reqOpts(options) }), "POST", path).runOrThrow(),
    put: (path, body, options) => chain(http.put(path, { body, ...reqOpts(options) }), "PUT", path).runOrThrow(),
    // Named `del`, not `delete`: `delete` is a reserved word, and a property access that only
    // works because it is quoted is a papercut every caller pays.
    del: (path, options) => chain(http.delete(path, reqOpts(options)), "DELETE", path).runOrThrow(),
    getEither: (path, options) => chain(http.get(path, reqOpts(options)), "GET", path).run(),
    postEither: (path, body, options) => chain(http.post(path, { body, ...reqOpts(options) }), "POST", path).run(),
    putEither: (path, body, options) => chain(http.put(path, { body, ...reqOpts(options) }), "PUT", path).run(),
    delEither: (path, options) => chain(http.delete(path, reqOpts(options)), "DELETE", path).run(),
  }
}
